<template>
    <page :busy="busy" :tabs="tabs" :compact-mobile-view="true">
        <template v-if="owner">
            <b-container fluid class="my-3" :style="{ height: currentTab !== documentsTab ? '100%' : 'auto' }">
                <div class="d-flex align-items-center">
                    <h4 class="mb-0">{{ owner.first_name + ' ' + owner.last_name }}</h4>
                    <b-button-group v-if="currentTab === documentsTab" class="ml-2">
                        <more-filters-dropdown class="agreements-dropdown" variant="outline-primary">
                            <form-square-switch
                                :labels="{
                                    checked: 'Display Rental Agreements to Owner',
                                    unchecked: 'Display Rental Agreements to Owner',
                                }"
                                v-model="owner.can_see_agreements"
                                @change="handleCanSeeAgreementsChange"
                                class="mb-1 agreements-toggle w-100"
                                :sync="true"
                            />
                        </more-filters-dropdown>
                    </b-button-group>
                </div>

                <hr />
                <div v-if="currentTab === detailsTab">
                    <owner-details-form
                        :initial-values="{
                            id: owner.id,
                            first_name: owner.first_name,
                            last_name: owner.last_name,
                            is_active: owner.is_active,
                            allow_edit_profile: owner.allow_edit_profile,
                            email: owner.client_email,
                            login_email: owner.login_email,
                            title: owner.title && owner.title.id,
                            unique_code: owner.unique_code,
                            company: owner.client_company,
                            mobile_phone: owner.mobile_phone,
                            address: owner.address,
                            tax_id: owner.tax_id,
                            pay_to: owner.pay_to,
                            notes: owner.notes,
                        }"
                    ></owner-details-form>
                </div>
                <div v-else-if="currentTab === accessTab">
                    <owner-edit-access :owner="owner"></owner-edit-access>
                </div>
                <div v-else-if="currentTab === propertiesTab" :style="{ height: '100%' }">
                    <owner-edit-properties :owner="owner"></owner-edit-properties>
                </div>
                <div v-else-if="currentTab === communicationTab" :style="{ height: '100%' }">
                    <owner-edit-communication :owner="owner"></owner-edit-communication>
                </div>
                <div v-else-if="currentTab === taxesTab">
                    <owner-edit-taxes :owner="owner"></owner-edit-taxes>
                </div>
                <div v-else-if="currentTab === documentsTab">
                    <owner-edit-documents :key="fileManagerKey" :owner="owner"></owner-edit-documents>
                </div>
            </b-container>
        </template>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import Page from '../../generic/page/page.vue';
import OwnerDetailsForm from '../common/owner-details-form.vue';
import OwnerEditAccess from './owner-edit-access/owner-edit-access.vue';
import OwnerEditCommunication from './owner-edit-communication/owner-edit-communication.vue';
import OwnerEditProperties from './owner-edit-properties/owner-edit-properties.vue';
import OwnerEditDocuments from './owner-edit-documents.vue';
import OwnerEditTaxes from './owner-edit-taxes.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';

export default {
    name: 'OwnerEdit',
    components: {
        Page,
        MoreFiltersDropdown,
        OwnerDetailsForm,
        OwnerEditAccess,
        OwnerEditCommunication,
        OwnerEditDocuments,
        OwnerEditProperties,
        OwnerEditTaxes,
        FormSquareSwitch,
    },
    computed: {
        ...mapGetters('owner', ['owner']),
        ownerID() {
            return parseInt(this.$route.params.id);
        },
        currentTab() {
            return this.tabs.findIndex((tab) => tab.active);
        },
        tabs() {
            return [
                {
                    title: `Owner List`,
                    route: `/app/owner/list/`,
                    active: false,
                },
                {
                    title: `Details`,
                    route: `/app/owner/${this.ownerID}/edit/`,
                    active: this.$route.path === `/app/owner/${this.ownerID}/edit/`,
                },
                {
                    title: `Access`,
                    route: `/app/owner/${this.ownerID}/edit/access/`,
                    active: this.$route.path === `/app/owner/${this.ownerID}/edit/access/`,
                },
                {
                    title: `Properties`,
                    route: `/app/owner/${this.ownerID}/edit/properties/`,
                    active: this.$route.path === `/app/owner/${this.ownerID}/edit/properties/`,
                },
                {
                    title: `Communication`,
                    route: `/app/owner/${this.ownerID}/edit/communication/`,
                    active: this.$route.path === `/app/owner/${this.ownerID}/edit/communication/`,
                },
                {
                    title: `Taxes`,
                    route: `/app/owner/${this.ownerID}/edit/taxes/`,
                    active: this.$route.path === `/app/owner/${this.ownerID}/edit/taxes/`,
                },
                {
                    title: `Documents`,
                    route: `/app/owner/${this.ownerID}/edit/documents/`,
                    active: this.$route.path === `/app/owner/${this.ownerID}/edit/documents/`,
                },
            ];
        },
    },
    data() {
        return {
            busy: false,
            detailsTab: 1,
            accessTab: 2,
            propertiesTab: 3,
            communicationTab: 4,
            taxesTab: 5,
            documentsTab: 6,
            fileManagerKey: 1,
        };
    },
    mounted() {
        this.$store.commit('owner/resetOwner');

        (async () => {
            this.busy = true;
            try {
                await this.fetchOwner();
            } finally {
                this.busy = false;
            }
        })();
    },
    beforeUnmount() {
        this.$store.commit('owner/resetOwner');
    },
    methods: {
        async fetchOwner() {
            try {
                await this.$store.dispatch('owner/fetchOwner', { id: this.ownerID });
            } catch (err) {
                this.errorToast({ text: 'Could not load owner data.' });
            }
        },
        async handleCanSeeAgreementsChange({ value }) {
            this.loading = true;
            try {
                await this.$store.dispatch('owner/enableDisableAgreements', { id: this.owner.id, enable: value });
                this.fileManagerKey += 1;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';
.agreements-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }

    .agreements-toggle {
        width: 280px !important;
        @include media-breakpoint-down(lg) {
            width: 290px;
        }
    }
}
</style>
